import { supabase } from '@/lib/supabase';

// Batch interfaces
export interface Batch {
  id: string;
  batch_name: string;
  course_id: string;
  start_date: string;
  end_date: string;
  schedule_info: {
    days: string[];
    time: string;
    timezone: string;
    zoom_url?: string;
  };
  max_students: number;
  current_students: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BatchWithDetails extends Batch {
  course_title?: string;
  instructor_name?: string;
}

export interface CreateBatchInput {
  batch_name: string;
  course_id: string;
  start_date: string;
  end_date: string;
  schedule_info: {
    days: string[];
    time: string;
    timezone: string;
    zoom_url?: string;
  };
  max_students: number;
}

export interface UpdateBatchInput extends Partial<CreateBatchInput> {
  is_active?: boolean;
}

/**
 * Fetch all batches with course and instructor details
 */
export async function getAllBatches(): Promise<{ data: BatchWithDetails[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('course_batches')
      .select(`
        *,
        courses(
          title,
          user_profiles(full_name)
        )
      `)
      .order('start_date', { ascending: true });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    const batches = data?.map((batch: any) => ({
      ...batch,
      course_title: batch.courses?.title,
      instructor_name: batch.courses?.user_profiles?.full_name
    })) || [];

    return { data: batches, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch batches') };
  }
}

/**
 * Fetch batches for a specific course
 */
export async function getBatchesByCourse(courseId: string): Promise<{ data: Batch[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('course_batches')
      .select('*')
      .eq('course_id', courseId)
      .eq('is_active', true)
      .order('start_date', { ascending: true });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data: data || [], error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch course batches') };
  }
}

/**
 * Create a new batch
 */
export async function createBatch(batchData: CreateBatchInput): Promise<{ data: Batch | null; error: Error | null }> {
  try {
    // Create the batch
    const { data, error } = await supabase
      .from('course_batches')
      .insert({
        batch_name: batchData.batch_name,
        course_id: batchData.course_id,
        start_date: batchData.start_date,
        end_date: batchData.end_date,
        schedule_info: batchData.schedule_info,
        max_students: batchData.max_students,
        current_students: 0,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    // Auto-assign existing enrollments without batch to this new batch
    const { error: linkError } = await linkEnrollmentsToBatch(data.id, batchData.course_id);
    if (linkError) {
      console.error('Failed to link enrollments to batch:', linkError);
      // Don't fail the batch creation, just log the error
    }

    return { data: data as Batch, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to create batch') };
  }
}

/**
 * Update an existing batch
 */
export async function updateBatch(
  batchId: string, 
  updates: UpdateBatchInput
): Promise<{ data: Batch | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('course_batches')
      .update(updates)
      .eq('id', batchId)
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data: data as Batch, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to update batch') };
  }
}

/**
 * Delete a batch (soft delete by setting is_active to false)
 */
export async function deleteBatch(batchId: string): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { error } = await supabase
      .from('course_batches')
      .update({ is_active: false })
      .eq('id', batchId);

    if (error) {
      return { success: false, error: new Error(error.message) };
    }

    return { success: true, error: null };
  } catch (error: any) {
    return { success: false, error: new Error(error?.message || 'Failed to delete batch') };
  }
}

/**
 * Get available batches for enrollment (future batches with available seats)
 */
export async function getAvailableBatches(courseId: string): Promise<{ data: Batch[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('course_batches')
      .select('*')
      .eq('course_id', courseId)
      .eq('is_active', true)
      .gt('start_date', new Date().toISOString())
      .lt('current_students', supabase.raw('max_students'))
      .order('start_date', { ascending: true });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data: data || [], error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch available batches') };
  }
}

/**
 * Get batch details by ID
 */
export async function getBatchById(batchId: string): Promise<{ data: BatchWithDetails | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('course_batches')
      .select(`
        *,
        courses(
          title,
          user_profiles(full_name)
        )
      `)
      .eq('id', batchId)
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    const batch = {
      ...data,
      course_title: data.courses?.title,
      instructor_name: data.courses?.user_profiles?.full_name
    };

    return { data: batch as BatchWithDetails, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch batch details') };
  }
}

/**
 * Link existing enrollments without batch_id to a specific batch
 */
export async function linkEnrollmentsToBatch(
  batchId: string,
  courseId: string
): Promise<{ success: boolean; error: Error | null; count: number }> {
  try {
    const { data, error } = await supabase
      .from('course_enrollments')
      .update({ batch_id: batchId })
      .eq('course_id', courseId)
      .is('batch_id', null)
      .eq('status', 'active')
      .select('id');

    if (error) {
      return { success: false, error: new Error(error.message), count: 0 };
    }

    const enrollmentCount = data?.length || 0;

    // Update current_students count in the batch
    if (enrollmentCount > 0) {
      await supabase
        .from('course_batches')
        .update({ current_students: supabase.raw(`current_students + ${enrollmentCount}`) })
        .eq('id', batchId);
    }

    return { success: true, error: null, count: enrollmentCount };
  } catch (error: any) {
    return { success: false, error: new Error(error?.message || 'Failed to link enrollments'), count: 0 };
  }
}

/**
 * Get batches for enrolled courses (shows all active batches for courses student is enrolled in)
 */
export async function getBatchesForEnrolledCourses(
  userId: string
): Promise<{ data: BatchWithDetails[] | null; error: Error | null }> {
  try {
    // First, get user's enrolled course IDs WITH VERIFIED PAYMENTS ONLY
    const { data: enrollments, error: enrollmentError } = await supabase
      .from('course_enrollments')
      .select(`
        course_id,
        payment:payment_transactions!course_enrollments_payment_transaction_id_fkey (
          status
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active');

    if (enrollmentError) {
      return { data: null, error: new Error(enrollmentError.message) };
    }

    if (!enrollments || enrollments.length === 0) {
      return { data: [], error: null };
    }

    // Filter enrollments to only include those with verified payments
    const verifiedEnrollments = enrollments.filter((enrollment: any) => {
      return enrollment.payment?.status === 'verified';
    });

    if (verifiedEnrollments.length === 0) {
      console.log('ℹ️ [BatchService] No enrollments with verified payments');
      return { data: [], error: null };
    }

    const courseIds = verifiedEnrollments.map((e: any) => e.course_id);

    // Fetch active batches for these courses
    const { data, error } = await supabase
      .from('course_batches')
      .select(`
        *,
        courses(
          title,
          user_profiles(full_name)
        )
      `)
      .in('course_id', courseIds)
      .eq('is_active', true)
      .order('start_date', { ascending: true });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    const batches = data?.map((batch: any) => ({
      ...batch,
      course_title: batch.courses?.title,
      instructor_name: batch.courses?.user_profiles?.full_name
    })) || [];

    console.log('✅ [BatchService] Fetched batches with payment verification:', {
      totalEnrollments: enrollments.length,
      verifiedEnrollments: verifiedEnrollments.length,
      batchesFound: batches.length
    });

    return { data: batches, error: null };
  } catch (error: any) {
    return { data: null, error: new Error(error?.message || 'Failed to fetch batches') };
  }
}
function batchService(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: batchService is not implemented yet.', args);
  return null;
}

export { batchService };